/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IProgressIndicatorStyleProps, IProgressIndicatorStyles, ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { globalMessages } from 'src/core/utils/globalMessages';
import { useCurrentVersionedAppId } from 'src/luis/modules/apps/hooks/useCurrentVersionedAppId';
import { CompositeEntitiesMigrationTrackerStatus } from 'src/luis/types/stores/entities/CompositeEntitiesMigrationTrackerStore';

export type EntityMigrationLoadingScreenProps = {
    /**
     * The new upgraded version name.
     */
    upgradedVersionName: string;

    /**
     * The progress percentage of the current migration
     * state.
     */
    migrationStateProgress: number;

    /**
     * The current state of the migration.
     */
    migrationState: CompositeEntitiesMigrationTrackerStatus;

    /**
     * Indicator whether the loading of migration process finished.
     */
    isLoadingFinished: boolean;
};

const messages = defineMessages({
    trainingMessage: {
        id: 'entityMigration.entityMigrationLoadingScreen.trainingMessage',
        defaultMessage: 'Training version "{ versionName }" ...'
    },
    creatingVersionMessage: {
        id: 'entityMigration.entityMigrationLoadingScreen.creatingVersionMessage',
        defaultMessage: 'Creating version "{ versionName }" ...'
    },
    loadingFinished: {
        id: 'entityMigration.entityMigrationLoadingScreen.loadingFinished',
        defaultMessage: 'Loading completed'
    }
});

const titleStyles = classNamesFunction<IProgressIndicatorStyleProps, IProgressIndicatorStyles>()({
    itemName: { fontSize: '12px', lineHeight: '16px' },
    progressBar: { height: '4px' },
    progressTrack: { height: '4px' }
});

export const EntityMigrationLoadingScreen = observer((props: EntityMigrationLoadingScreenProps) => {
    const { migrationState, migrationStateProgress, upgradedVersionName, isLoadingFinished } = props;
    const { rootStore } = useGlobalState();
    const { localize } = useLocalization();
    const { appId } = useCurrentVersionedAppId();

    const oldVersionTrainingStore = rootStore.currentLuisAppStore.currentAppVersionStore?.trainingStore;
    const upgradedVersionTrainingStore = rootStore.luisAppStoreHouse.findById(appId)?.appVersionStoreHouse.findById(upgradedVersionName)
        ?.trainingStore;
    const isOldVersionTraining = oldVersionTrainingStore.serviceStatus !== 'ready';
    let currentDisplayedMessage = localize(globalMessages.loading);
    let currentProgressPercentage: number = null;

    if (isOldVersionTraining) {
        currentDisplayedMessage = localize(messages.trainingMessage, {
            versionName: rootStore.currentLuisAppStore.currentAppVersionStore.id
        });

        const status = oldVersionTrainingStore.currentTrainingStatus;
        currentProgressPercentage = (status.trainedModelsCount / status.totalModelsCount) * 100;
    }

    if (migrationState === 'creatingNewVersion') {
        currentDisplayedMessage = localize(messages.creatingVersionMessage, { versionName: upgradedVersionName });
        currentProgressPercentage = migrationStateProgress;
    }

    if (migrationState === 'trainingNewVersion') {
        currentDisplayedMessage = localize(messages.trainingMessage, { versionName: upgradedVersionName });

        const status = upgradedVersionTrainingStore.currentTrainingStatus;
        currentProgressPercentage = (status.trainedModelsCount / status.totalModelsCount) * 100;
    }

    if (isLoadingFinished) {
        currentDisplayedMessage = localize(messages.loadingFinished);
        currentProgressPercentage = 100;
    }

    return (
        <Stack verticalFill verticalAlign="center" styles={{ root: { minHeight: '388px' } }} tokens={{ padding: '0 130px' }}>
            <ProgressIndicator styles={titleStyles} label={currentDisplayedMessage} percentComplete={currentProgressPercentage / 100} />
        </Stack>
    );
});
